/**
 * Recovery for games recorded while the ascending Clock Type was wrongly
 * applied to the game clock itself (core 0.11.7–0.11.9): their clock-fed
 * values were persisted as elapsed time where the canonical form is
 * remaining time.
 *
 * Detection is per play via the frozen clock in its context snapshot —
 * `contextSnapshot.gameClock.direction === "up"` marks a play recorded
 * under an up-counting clock. The transform (limit - value) is
 * self-inverse, so it is gated strictly on that marker and the marker is
 * flipped to "down" after converting, which makes the whole pass
 * idempotent.
 *
 * All entities are mutated in place; callers persist them.
 */

const isUpMarked = (play) => {
    const clock = play?.contextSnapshot?.gameClock;
    return Boolean(
        clock &&
            typeof clock.referenceValue === "number" &&
            clock.direction === "up"
    );
};

const mirror = (ms, limitMs) => Math.max(0, Math.min(limitMs, limitMs - ms));

const convertTimeRef = (timeRef, limitMs) => {
    if (timeRef && typeof timeRef.milliseconds === "number") {
        timeRef.milliseconds = mirror(timeRef.milliseconds, limitMs);
    }
};

const convertPlay = (play, fragments) => {
    const clock = play.contextSnapshot.gameClock;
    // The frozen clock carries the period length it ran under, so each
    // play mirrors against its own limit (overtime-safe).
    const limitMs = clock.timeLimitInMilliseconds || 0;

    clock.referenceValue = mirror(clock.referenceValue, limitMs);
    clock.direction = "down";
    if (typeof play.time === "number") {
        play.time = mirror(play.time, limitMs);
    }

    fragments.forEach((fragment) => {
        // Every fragment field that stores a clock reading:
        // CollectDriveTime.time / Timeout.time / SetTimeAndPeriod
        // .timeAndPeriod / legacy StartDrive.startTime — all shaped
        // { period, milliseconds }.
        convertTimeRef(fragment.time, limitMs);
        convertTimeRef(fragment.timeAndPeriod, limitMs);
        convertTimeRef(fragment.startTime, limitMs);
    });

    return limitMs;
};

/**
 * Convert every up-marked play (and its fragments and drives) of a game
 * back to canonical remaining time. No-op for healthy games.
 *
 * @param {object} options
 * @param {Play[]} options.plays - plays of the game; fragments may be
 *  attached (play.fragments) or supplied separately
 * @param {Fragment[]} [options.fragments] - flat fragment list (sync
 *  path); matched to plays by playId. Ignored when plays carry their own.
 * @param {Drive[]} [options.drives] - drives of the game
 * @returns {{ converted: boolean, convertedPlayIds: string[], skippedDriveIds: string[] }}
 */
export const recoverUpCountingClockData = ({
    plays = [],
    fragments = [],
    drives = []
}) => {
    const fragmentsByPlayId = new Map();
    fragments.forEach((fragment) => {
        const list = fragmentsByPlayId.get(fragment.playId) || [];
        list.push(fragment);
        fragmentsByPlayId.set(fragment.playId, list);
    });

    const convertedPlayIds = [];
    const limitByDriveId = new Map();
    const playsByDriveId = new Map();

    plays.forEach((play) => {
        if (play.driveId) {
            const list = playsByDriveId.get(play.driveId) || [];
            list.push(play);
            playsByDriveId.set(play.driveId, list);
        }
        if (isUpMarked(play)) {
            const playFragments = play.fragments?.length
                ? play.fragments
                : fragmentsByPlayId.get(play.id) || [];
            const limitMs = convertPlay(play, playFragments);
            convertedPlayIds.push(play.id);
            if (play.driveId) {
                limitByDriveId.set(play.driveId, limitMs);
            }
        }
    });

    // A drive converts only when every one of its plays was up-marked —
    // for a drive that straddled a mid-game toggle the stored start/end
    // mix both forms and no blind transform is safe; those are reported
    // for manual review instead.
    const convertedSet = new Set(convertedPlayIds);
    const skippedDriveIds = [];
    if (convertedPlayIds.length) {
        drives.forEach((drive) => {
            const drivePlays = playsByDriveId.get(drive.id) || [];
            const touched = drivePlays.some((play) =>
                convertedSet.has(play.id)
            );
            if (!touched) return;

            const allConverted = drivePlays.every((play) =>
                convertedSet.has(play.id)
            );
            const limitMs = limitByDriveId.get(drive.id) || 0;
            if (allConverted && limitMs) {
                if (typeof drive.startTime === "number") {
                    drive.startTime = mirror(drive.startTime, limitMs);
                }
                if (typeof drive.endTime === "number") {
                    drive.endTime = mirror(drive.endTime, limitMs);
                }
            } else {
                skippedDriveIds.push(drive.id);
            }
        });
    }

    return {
        converted: convertedPlayIds.length > 0,
        convertedPlayIds,
        skippedDriveIds
    };
};
